import { useQuery } from "@apollo/client";
import { Button, Chip, Stack, styled, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";

import Loading from "../common/Loading";
import { useLogging } from "../logging/useLogging";
import { pages } from "../pages/pages";
import { captureException } from "../sentry";
import theme from "../styles/theme";
import { AiCategory, AiExerciseDataState, AppEvent, Category } from "../types";
import { getAiExerciseIdFromName, getAiExerciseNameFromId, isAiExerciseId } from "../utils/exercises";
import { getSettings } from "../utils/localStorage";
import { calculateTimePassedSeconds } from "../utils/timeHelpers";
import { useBlocker } from "../utils/useBlocker";
import { useFavouritesAndExcluded } from "../utils/useFavouritesAndExcluded";
import { generateExerciseQuery, GenerateExerciseQueryType, GenerateExerciseQueryVariables } from "./AiExercise.gql";
import { AiImages } from "./AiImages";
import EnvironmentDescription from "./EnvironmentDescription";
import { ExerciseName, MediaContainer, StyledImage } from "./Exercise.sc";
import ExerciseTemplate from "./ExerciseTemplate";
import { convertUrlEncodedPhotoToBlob } from "./utils/photoBlob";
import { useExerciseSessionWrapper } from "./utils/useExerciseSessionWrapper";

const AiExerciseSession: React.FunctionComponent = () => {
    const location = useLocation();

    const { photo, settings } = (location.state as AiExerciseDataState | undefined) ?? { settings: getSettings() };

    const blobPhoto = useMemo(() => convertUrlEncodedPhotoToBlob(photo), [photo]);

    const [history, setHistory] = useState<ReadonlyArray<string>>([]);

    const { favourites, excluded } = useFavouritesAndExcluded();

    const categories = useMemo<AiCategory[]>(() => {
        const categories = settings.categories ?? [];
        if (settings.discreet === "yes") {
            return [...categories, "discreet"];
        }
        return [...categories];
    }, [settings]);
    const favoriteVariable = useMemo(
        () => (settings.favourite === "yes" ? favourites.filter(isAiExerciseId).map(getAiExerciseNameFromId) ?? null : null),
        [favourites, settings.favourite],
    );
    const excludedVariable = useMemo(() => excluded.filter(isAiExerciseId).map(getAiExerciseNameFromId) ?? null, [excluded]);

    const { logWithPayload } = useLogging();

    const { data, loading, error, refetch } = useQuery<GenerateExerciseQueryType, GenerateExerciseQueryVariables>(generateExerciseQuery, {
        variables: {
            photo: blobPhoto,
            categories: categories,
            history: null,
            favorite: favoriteVariable,
            excluded: excludedVariable,
        },
        skip: !photo || !categories,
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
            captureException(error);
        },
        onCompleted: (data) => {
            const name = data.generateExercise.exercise?.name;
            if (name) {
                const id = getAiExerciseIdFromName(name);
                setHistory((prev) => [...prev, name]);
                logWithPayload(AppEvent.ExerciseStarted, { id });
            }
        },
    });

    const pollingId = data?.generateExercise?.polling_id;

    const [startTime, setStartTime] = useState<Date>(new Date());

    // reset start time for each new exercise
    React.useEffect(() => {
        setStartTime(new Date());
    }, [data]);

    const { addExercise, finishSessionAndNavigateToSummary } = useExerciseSessionWrapper();

    useBlocker();

    const navigate = useNavigate();

    const handleNextExercise = (skip?: boolean) => {
        if (!skip && data?.generateExercise?.exercise?.name) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const id = getAiExerciseIdFromName(data?.generateExercise?.exercise?.name);
            addExercise({ id, duration: calculateTimePassedSeconds(startTime) });
        }
        refetch({ history });
    };

    const beforeEndButtonClick = async () => {
        if (data?.generateExercise?.exercise?.name) {
            const id = getAiExerciseIdFromName(data?.generateExercise?.exercise?.name);
            await finishSessionAndNavigateToSummary({ id, duration: calculateTimePassedSeconds(startTime) });
        } else {
            await finishSessionAndNavigateToSummary(undefined);
        }
    };

    useEffect(() => {
        if (!photo || !categories) {
            navigate(pages.ai_exercise);
        }
        logWithPayload(AppEvent.AiSessionStarted, { categories, favourites, excluded });
        // photo and categories won't change because they can only be set in the parent AiExercise component
        // logWithPayload should never change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRetakePhoto = () => {
        navigate(pages.ai_exercise);
    };

    const computedError = error?.message === "too_many_requests" ? "Too many requests" : error?.message ?? data?.generateExercise.error;

    return (
        <ExerciseTemplate
            onBeforeEndButtonClick={beforeEndButtonClick}
            onNextButtonClick={handleNextExercise}
            loading={loading}
            disableNextButton={!!computedError}
        >
            {loading ? (
                <Loading />
            ) : (
                <div>
                    <Chip
                        style={{
                            backgroundColor: theme.colors[Category.Ai],
                            color: "white",
                            marginTop: 10,
                            marginBottom: 10,
                        }}
                        label={"AI Generated" /* TODO: I18N */}
                    />
                    {data?.generateExercise.exercise && (
                        <>
                            <ExerciseName variant="h4" gutterBottom>
                                {data?.generateExercise?.exercise?.name}
                            </ExerciseName>
                            <AiImages pollingId={pollingId} />
                            <ReactMarkdown>{data?.generateExercise?.exercise?.instructions ?? ""}</ReactMarkdown>
                        </>
                    )}
                    {computedError && (
                        <Stack justifyItems="center" alignItems="start">
                            <Typography>
                                {/* TODO: I18N */}
                                There was an error:
                                <br /> {computedError}
                            </Typography>
                            <Button variant="contained" onClick={handleRetakePhoto}>
                                Retake photo
                            </Button>
                        </Stack>
                    )}
                    {data && (
                        <MediaContainerPadded>
                            <StyledImage src={URL.createObjectURL(blobPhoto)}></StyledImage>
                        </MediaContainerPadded>
                    )}
                </div>
            )}
            <EnvironmentDescription photo={blobPhoto} notVisible={loading} />
        </ExerciseTemplate>
    );
};

export default AiExerciseSession;

const MediaContainerPadded = styled(MediaContainer)`
    padding-top: 20px;
`;
